import { CommonModule } from '@angular/common'
import { Component, computed, effect, ElementRef, HostListener, inject, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { NavigationStart, Router } from '@angular/router'
import { filter } from 'rxjs/operators'
import { CoachmarkPlacement } from '@models/coachmark.model'
import { CoachmarkService } from '@services/coachmark.service'

interface TooltipPosition {
  left: number | null
  top: number | null
  placement: CoachmarkPlacement
  /** Offset (px) of the arrow along the tooltip edge, from its start. */
  arrow: number | null
  mobile: boolean
}

/**
 * Single, app-wide renderer for the active coachmark tour.
 *
 * Reads {@link CoachmarkService} signals and draws a dark overlay with a rounded
 * "spotlight" hole over the current target plus a positioned tooltip. The target
 * rect is tracked every frame (via requestAnimationFrame) while a tour is active,
 * so it stays glued to the element through scrolling, layout shifts and
 * animations. Mount it once at the application root.
 */
@Component({
  selector: 'mh-coachmark-overlay',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './coachmark-overlay.component.html',
  styleUrl: './coachmark-overlay.component.scss',
})
export class CoachmarkOverlayComponent {
  cm = inject(CoachmarkService)
  private router = inject(Router)
  private host = inject(ElementRef<HTMLElement>)

  readonly rect = signal<DOMRect | null>(null)
  readonly viewport = signal({ w: window.innerWidth, h: window.innerHeight })
  private readonly tooltipSize = signal({ w: 320, h: 160 })

  private rafId = 0
  private lastIndex = -1

  readonly step = this.cm.currentStep
  readonly isMobile = computed(() => this.viewport().w <= 640)
  readonly dots = computed(() => Array.from({ length: this.cm.stepCount() }))

  constructor() {
    // Tearing down on navigation prevents a stale spotlight from lingering.
    this.router.events
      .pipe(
        takeUntilDestroyed(),
        filter(e => e instanceof NavigationStart)
      )
      .subscribe(() => this.cm.clear())

    effect(() => {
      if (this.cm.isActive()) {
        this.startLoop()
      } else {
        this.stopLoop()
      }
    })
  }

  @HostListener('window:resize')
  onResize(): void {
    this.viewport.set({ w: window.innerWidth, h: window.innerHeight })
  }

  // ---------------------------------------------------------------------------
  // Geometry derived from the tracked target rect
  // ---------------------------------------------------------------------------

  /** SVG path for the dark backdrop with a rounded-rect hole (even-odd fill). */
  readonly maskPath = computed(() => {
    const r = this.rect()
    const vp = this.viewport()
    if (!r) return ''
    const hole = this.holeRect()
    const { x, y, w, h } = hole
    const rr = Math.min(hole.radius, w / 2, h / 2)
    const outer = `M0 0 H${vp.w} V${vp.h} H0 Z`
    const inner =
      `M${x + rr} ${y} ` +
      `H${x + w - rr} A${rr} ${rr} 0 0 1 ${x + w} ${y + rr} ` +
      `V${y + h - rr} A${rr} ${rr} 0 0 1 ${x + w - rr} ${y + h} ` +
      `H${x + rr} A${rr} ${rr} 0 0 1 ${x} ${y + h - rr} ` +
      `V${y + rr} A${rr} ${rr} 0 0 1 ${x + rr} ${y} Z`
    return `${outer} ${inner}`
  })

  readonly ringStyle = computed(() => {
    const hole = this.holeRect()
    return {
      left: `${hole.x}px`,
      top: `${hole.y}px`,
      width: `${hole.w}px`,
      height: `${hole.h}px`,
      'border-radius': `${hole.radius}px`,
    }
  })

  readonly tooltipPos = computed<TooltipPosition | null>(() => {
    const r = this.rect()
    if (!r) return null

    const step = this.step()
    const preferred: CoachmarkPlacement = step?.placement ?? 'auto'

    if (this.isMobile()) {
      return { left: null, top: null, placement: 'bottom', arrow: null, mobile: true }
    }

    const vp = this.viewport()
    const ts = this.tooltipSize()
    const gap = 14
    const margin = 12
    const hole = this.holeRect()

    const space = {
      top: hole.y,
      bottom: vp.h - (hole.y + hole.h),
      left: hole.x,
      right: vp.w - (hole.x + hole.w),
    }

    const order: CoachmarkPlacement[] =
      preferred === 'auto'
        ? ['bottom', 'top', 'right', 'left']
        : [preferred, 'bottom', 'top', 'right', 'left']

    const fits = (p: CoachmarkPlacement): boolean => {
      if (p === 'bottom') return space.bottom >= ts.h + gap
      if (p === 'top') return space.top >= ts.h + gap
      if (p === 'right') return space.right >= ts.w + gap
      if (p === 'left') return space.left >= ts.w + gap
      return false
    }

    const placement = order.find(fits) ?? 'bottom'
    const centerX = hole.x + hole.w / 2
    const centerY = hole.y + hole.h / 2

    let left: number
    let top: number

    if (placement === 'bottom' || placement === 'top') {
      left = centerX - ts.w / 2
      top = placement === 'bottom' ? hole.y + hole.h + gap : hole.y - ts.h - gap
    } else {
      top = centerY - ts.h / 2
      left = placement === 'right' ? hole.x + hole.w + gap : hole.x - ts.w - gap
    }

    left = Math.max(margin, Math.min(left, vp.w - ts.w - margin))
    top = Math.max(margin, Math.min(top, vp.h - ts.h - margin))

    // Arrow points back at the target centre, clamped within the tooltip edge.
    const arrow =
      placement === 'bottom' || placement === 'top'
        ? Math.max(16, Math.min(centerX - left, ts.w - 16))
        : Math.max(16, Math.min(centerY - top, ts.h - 16))

    return { left, top, placement, arrow, mobile: false }
  })

  // ---------------------------------------------------------------------------
  // Tracking loop
  // ---------------------------------------------------------------------------

  private holeRect() {
    const r = this.rect()
    const vp = this.viewport()
    const step = this.step()
    const pad = step?.padding ?? 8
    const radius = step?.radius ?? 10
    if (!r) return { x: 0, y: 0, w: 0, h: 0, radius }
    const x = Math.max(0, r.left - pad)
    const y = Math.max(0, r.top - pad)
    const w = Math.min(vp.w - x, r.width + pad * 2)
    const h = Math.min(vp.h - y, r.height + pad * 2)
    return { x, y, w, h, radius }
  }

  private startLoop(): void {
    if (this.rafId) return
    this.lastIndex = -1
    this.rafId = requestAnimationFrame(this.tick)
  }

  private stopLoop(): void {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId)
      this.rafId = 0
    }
    this.rect.set(null)
    this.lastIndex = -1
  }

  private readonly tick = (): void => {
    if (!this.cm.isActive()) {
      this.stopLoop()
      return
    }

    const el = this.cm.resolveCurrentElement()
    if (!el) {
      // Target disappeared (e.g. removed from the DOM) — move on.
      this.cm.next()
      this.rafId = requestAnimationFrame(this.tick)
      return
    }

    const idx = this.cm.currentIndex()
    if (idx !== this.lastIndex) {
      this.lastIndex = idx
      el.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' })
    }

    this.rect.set(el.getBoundingClientRect())
    this.measureTooltip()

    this.rafId = requestAnimationFrame(this.tick)
  }

  private measureTooltip(): void {
    const tip = this.host.nativeElement.querySelector('.cm-tooltip') as HTMLElement | null
    if (!tip) return
    const r = tip.getBoundingClientRect()
    if (r.width === 0 && r.height === 0) return
    const current = this.tooltipSize()
    if (Math.abs(current.w - r.width) > 1 || Math.abs(current.h - r.height) > 1) {
      this.tooltipSize.set({ w: r.width, h: r.height })
    }
  }
}
